// src/components/FormSerieEntrenamiento.js
// Modal para crear/editar series de entrenamientos recurrentes

import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, MapPin, Users, AlertCircle, Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { createSerieEntrenamiento, updateSerieEntrenamiento, getTemporadas, getOrgDeportes } from '../lib/supabase';
import { opcionesDeporte } from '../lib/deportesOrg';
import { useOrganization } from '../contexts/OrganizationContext';
import { parseLocalDate, getMexicoDateString } from '../lib/dateUtils';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';

const DIAS_SEMANA = [
  { value: 'lunes', label: 'Lunes', short: 'L' },
  { value: 'martes', label: 'Martes', short: 'M' },
  { value: 'miercoles', label: 'Miércoles', short: 'X' },
  { value: 'jueves', label: 'Jueves', short: 'J' },
  { value: 'viernes', label: 'Viernes', short: 'V' },
  { value: 'sabado', label: 'Sábado', short: 'S' },
  { value: 'domingo', label: 'Domingo', short: 'D' }
];

export default function FormSerieEntrenamiento({
  open = false,
  onOpenChange,
  serie = null,
  grupos = [],
  onSuccess
}) {
  const { org } = useOrganization();
  const guard = useUnsavedGuard({ onClose: () => onOpenChange && onOpenChange(false) });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [temporadas, setTemporadas] = useState([]);
  const [orgDeportes, setOrgDeportes] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [deporteFilter, setDeporteFilter] = useState('todos');

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    grupo_id: '',
    temporada_id: '',
    dias_semana: [],
    hora_inicio: '19:00',
    hora_fin: '20:30',
    fecha_inicio: getMexicoDateString(),
    fecha_fin: '',
    ubicacion: '',
    activo: true
  });

  const isEditing = Boolean(serie);

  // Cargar temporadas
  useEffect(() => {
    const loadData = async () => {
      const [tempRes, depRes] = await Promise.all([
        getTemporadas(true),
        getOrgDeportes(org?.id),
      ]);
      if (tempRes.data) setTemporadas(tempRes.data);
      if (depRes.data) setOrgDeportes(depRes.data);
    };
    if (open) {
      loadData();
    }
  }, [open, org?.id]);

  // Si estamos editando, cargar datos de la serie
  useEffect(() => {
    if (serie) {
      setFormData({
        nombre: serie.nombre || '',
        descripcion: serie.descripcion || '',
        grupo_id: serie.grupo_id || '',
        temporada_id: serie.temporada_id || '',
        dias_semana: serie.dias_semana || [],
        hora_inicio: serie.hora_inicio || '19:00',
        hora_fin: serie.hora_fin || '20:30',
        fecha_inicio: serie.fecha_inicio || getMexicoDateString(),
        fecha_fin: serie.fecha_fin || '',
        ubicacion: serie.ubicacion || '',
        activo: serie.activo !== false
      });
      setSelectedGroupIds(serie.grupo_id ? [serie.grupo_id] : []);
    } else {
      // Reset form when opening for new serie
      setFormData({
        nombre: '',
        descripcion: '',
        grupo_id: '',
        temporada_id: '',
        dias_semana: [],
        hora_inicio: '19:00',
        hora_fin: '20:30',
        fecha_inicio: getMexicoDateString(),
        fecha_fin: '',
        ubicacion: '',
        activo: true
      });
      setSelectedGroupIds([]);
    }
    setGroupSearchTerm('');
    setError(null);
    guard.resetDirty();
  }, [serie, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDiaClick = (dia) => {
    guard.markDirty();
    setFormData(prev => ({
      ...prev,
      dias_semana: prev.dias_semana.includes(dia)
        ? prev.dias_semana.filter(d => d !== dia)
        : [...prev.dias_semana, dia]
    }));
  };

  const handleToggleGroup = (groupId) => {
    guard.markDirty();
    setSelectedGroupIds(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      }
      return [...prev, groupId];
    });
  };

  const handleSelectAllFiltered = () => {
    guard.markDirty();
    const allFilteredIds = filteredGroups.map((g) => g.id);
    const allSelected = allFilteredIds.every((id) => selectedGroupIds.includes(id));
    if (allSelected) {
      // Deselect all filtered
      setSelectedGroupIds((prev) => prev.filter((id) => !allFilteredIds.includes(id)));
    } else {
      // Select all filtered (merge with existing)
      setSelectedGroupIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
    }
  };

  const deporteOptions = useMemo(() => opcionesDeporte(orgDeportes), [orgDeportes]);

  const filteredGroups = useMemo(() => {
    const term = groupSearchTerm.trim().toLowerCase();
    return grupos
      .filter((grupo) => {
        if (deporteFilter !== 'todos' && grupo.deporte !== deporteFilter) return false;
        if (!term) return true;
        return (grupo.nombre_del_grupo || '').toLowerCase().includes(term) ||
          (grupo.rama || '').toLowerCase().includes(term);
      })
      .sort((a, b) => (a.nombre_del_grupo || '').localeCompare(b.nombre_del_grupo || ''));
  }, [grupos, groupSearchTerm, deporteFilter]);

  const selectedGroupsDetailed = useMemo(
    () => grupos.filter((grupo) => selectedGroupIds.includes(grupo.id)),
    [grupos, selectedGroupIds]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.nombre.trim()) {
      setError('El nombre es requerido');
      return;
    }

    if (isEditing) {
      if (!formData.grupo_id) {
        setError('Debes seleccionar un grupo');
        return;
      }
    } else if (selectedGroupIds.length === 0) {
      setError('Debes seleccionar al menos un grupo');
      return;
    }

    if (formData.dias_semana.length === 0) {
      setError('Debes seleccionar al menos un día de la semana');
      return;
    }

    if (formData.hora_inicio >= formData.hora_fin) {
      setError('La hora de fin debe ser posterior a la hora de inicio');
      return;
    }

    if (formData.fecha_fin && formData.fecha_fin < formData.fecha_inicio) {
      setError('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    setLoading(true);

    try {
      // Preparar datos para enviar (convertir valores vacíos a null)
      // Excluir 'ubicacion' ya que no existe en la tabla series_entrenamiento
      const { ubicacion, ...formDataWithoutUbicacion } = formData;
      const dataToSend = {
        ...formDataWithoutUbicacion,
        fecha_fin: formData.fecha_fin || null,
        temporada_id: formData.temporada_id || null
      };

      if (serie) {
        const result = await updateSerieEntrenamiento(serie.id, dataToSend);
        if (result.error) {
          throw result.error;
        }
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        const pendingGroups = selectedGroupIds;
        const createdSeries = [];

        for (const groupId of pendingGroups) {
          const result = await createSerieEntrenamiento({
            ...dataToSend,
            grupo_id: groupId
          });

          if (result.error) {
            throw result.error;
          }
          createdSeries.push(result.data);
        }

        if (onSuccess) {
          onSuccess(createdSeries);
        }
      }

      // Cerrar modal
      guard.resetDirty();
      if (onOpenChange) {
        onOpenChange(false);
      }
    } catch (err) {
      console.error('Error guardando serie:', err);
      setError(err.message || 'Error al guardar el horario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {serie ? 'Editar horario de entrenamiento' : 'Nuevo horario de entrenamiento'}
          </DialogTitle>
          <DialogDescription>
            {serie ? 'Modifica los días y horas del horario' : 'Define los días y horas en que entrena el grupo'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre *</Label>
            <Input
              id="nombre"
              value={formData.nombre}
              onChange={(e) => { guard.markDirty(); setFormData(prev => ({ ...prev, nombre: e.target.value })); }}
              placeholder="Ej: Entrenamiento semanal"
              required
            />
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => { guard.markDirty(); setFormData(prev => ({ ...prev, descripcion: e.target.value })); }}
              placeholder="Descripción opcional del entrenamiento..."
              className="min-h-[80px]"
            />
          </div>

          {/* Grupo(s) */}
          {isEditing ? (
            <div className="space-y-2">
              <Label htmlFor="grupo">Grupo *</Label>
              <Select
                value={formData.grupo_id}
                onValueChange={(value) => {
                  guard.markDirty();
                  setFormData(prev => ({ ...prev, grupo_id: value }));
                  setSelectedGroupIds(value ? [value] : []);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un grupo" />
                </SelectTrigger>
                <SelectContent>
                  {filteredGroups.map(grupo => (
                    <SelectItem key={grupo.id} value={grupo.id}>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {grupo.nombre_del_grupo}
                        {grupo.rama && ` - ${grupo.rama}`}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {deporteOptions.length > 0 && (
                <Select value={deporteFilter} onValueChange={setDeporteFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filtrar por deporte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los deportes</SelectItem>
                    {deporteOptions.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Grupos *</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      placeholder="Buscar grupo..."
                      value={groupSearchTerm}
                      onChange={(e) => setGroupSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                    <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                  <Select value={deporteFilter} onValueChange={setDeporteFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Deporte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {deporteOptions.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  {filteredGroups.length > 0 && (
                    <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={handleSelectAllFiltered}>
                      {filteredGroups.every((g) => selectedGroupIds.includes(g.id))
                        ? `Deseleccionar todos (${filteredGroups.length})`
                        : `Seleccionar todos (${filteredGroups.length})`
                      }
                    </Button>
                  )}
                  <span className="text-xs text-muted-foreground">{selectedGroupIds.length} seleccionado(s)</span>
                </div>
                {selectedGroupsDetailed.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedGroupsDetailed.map((grupo) => (
                      <Badge key={grupo.id} variant="secondary" className="flex items-center gap-2">
                        <span>{grupo.nombre_del_grupo}</span>
                        <button
                          type="button"
                          onClick={() => handleToggleGroup(grupo.id)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="border rounded-lg max-h-56 overflow-y-auto divide-y">
                  {filteredGroups.length === 0 ? (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      No hay grupos que coincidan con la búsqueda.
                    </div>
                  ) : (
                    filteredGroups.map((grupo) => {
                      const isChecked = selectedGroupIds.includes(grupo.id);
                      return (
                        <label
                          key={grupo.id}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm hover:bg-muted/50 ${
                            isChecked ? 'bg-accent' : ''
                          }`}
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => handleToggleGroup(grupo.id)}
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">{grupo.nombre_del_grupo}</span>
                            <span className="text-xs text-muted-foreground">
                              {grupo.rama || 'Sin rama'}
                            </span>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {selectedGroupIds.length > 0
                    ? `Se creará${selectedGroupIds.length > 1 ? 'n' : ''} ${selectedGroupIds.length} horario${selectedGroupIds.length > 1 ? 's' : ''} (uno por grupo seleccionado).`
                    : 'Selecciona uno o más grupos; se creará el mismo horario para cada uno.'}
                </p>
              </div>
            </div>
          )}

          {/* Temporada */}
          <div className="space-y-2">
            <Label htmlFor="temporada">Temporada (opcional)</Label>
            <Select
              value={formData.temporada_id || undefined}
              onValueChange={(value) => { guard.markDirty(); setFormData(prev => ({ ...prev, temporada_id: value === 'none' ? '' : value })); }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sin temporada" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin temporada</SelectItem>
                {temporadas.map(temporada => (
                  <SelectItem key={temporada.id} value={temporada.id}>
                    {temporada.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Clasifica este horario dentro de una temporada específica
            </p>
          </div>

          {/* Días de la semana */}
          <div className="space-y-3">
            <Label>Días de la Semana *</Label>
            <div className="grid grid-cols-7 gap-2">
              {DIAS_SEMANA.map(dia => {
                const isSelected = formData.dias_semana.includes(dia.value);
                return (
                  <button
                    key={dia.value}
                    type="button"
                    onClick={() => handleDiaClick(dia.value)}
                    className={`
                      px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isSelected
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }
                    `}
                    title={dia.label}
                  >
                    {dia.short}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Seleccionados: {formData.dias_semana.length > 0
                ? formData.dias_semana.map(d => DIAS_SEMANA.find(dia => dia.value === d)?.label).join(', ')
                : 'Ninguno'}
            </p>
          </div>

          {/* Horarios */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hora_inicio">
                <Clock className="w-4 h-4 inline mr-1" />
                Hora Inicio *
              </Label>
              <Input
                id="hora_inicio"
                type="time"
                value={formData.hora_inicio}
                onChange={(e) => { guard.markDirty(); setFormData(prev => ({ ...prev, hora_inicio: e.target.value })); }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hora_fin">
                <Clock className="w-4 h-4 inline mr-1" />
                Hora Fin *
              </Label>
              <Input
                id="hora_fin"
                type="time"
                value={formData.hora_fin}
                onChange={(e) => { guard.markDirty(); setFormData(prev => ({ ...prev, hora_fin: e.target.value })); }}
                required
              />
            </div>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fecha_inicio">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha Inicio *
              </Label>
              <Input
                id="fecha_inicio"
                type="date"
                value={formData.fecha_inicio}
                onChange={(e) => { guard.markDirty(); setFormData(prev => ({ ...prev, fecha_inicio: e.target.value })); }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fecha_fin">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha Fin (opcional)
              </Label>
              <Input
                id="fecha_fin"
                type="date"
                value={formData.fecha_fin}
                onChange={(e) => { guard.markDirty(); setFormData(prev => ({ ...prev, fecha_fin: e.target.value })); }}
              />
              <p className="text-xs text-muted-foreground">
                Dejar vacío para entrenamientos sin fecha de fin
              </p>
            </div>
          </div>

          {/* Ubicación */}
          <div className="space-y-2">
            <Label htmlFor="ubicacion">
              <MapPin className="w-4 h-4 inline mr-1" />
              Ubicación
            </Label>
            <Input
              id="ubicacion"
              value={formData.ubicacion}
              onChange={(e) => { guard.markDirty(); setFormData(prev => ({ ...prev, ubicacion: e.target.value })); }}
              placeholder="Ej: Cancha Principal"
            />
          </div>

          {/* Resumen */}
          {formData.dias_semana.length > 0 && (
            <Card className="bg-muted/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Resumen</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <p><strong>Días:</strong> {formData.dias_semana.map(d => DIAS_SEMANA.find(dia => dia.value === d)?.label).join(', ')}</p>
                <p><strong>Horario:</strong> {formData.hora_inicio} - {formData.hora_fin}</p>
                <p><strong>Desde:</strong> {parseLocalDate(formData.fecha_inicio)?.toLocaleDateString('es-MX')}</p>
                {formData.fecha_fin && (
                  <p><strong>Hasta:</strong> {parseLocalDate(formData.fecha_fin)?.toLocaleDateString('es-MX')}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Botones */}
          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Guardando...' : serie ? 'Actualizar horario' : 'Crear horario'}
            </Button>
          </div>
        </form>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  );
}



